import { useEffect, useRef, useState } from "react";
import StaticBoard, {
  type DisplayBoardArrow,
  type DisplayBoardHighlight,
} from "../../chessBoard/components/DisplayBoard";
import BlogBoardFrame from "./BlogBoardFrame";

const DEFAULT_SIZE = 520;
const MINIMUM_SIZE = 220;
const DEFAULT_ARROW_COLOR = "#f97316";

export type ArticleBoardProps = {
  fen: string;
  title?: string;
  caption?: string;
  playingWhite?: boolean;
  boardSize?: number;
  edgeColor?: string;
  arrows?: DisplayBoardArrow[];
  highlights?: DisplayBoardHighlight[];
  presentation?: "body" | "hero";
};

/**
 * Responsive, non-interactive board presentation for article content. The
 * underlying board is the same deterministic renderer used by Board Studio,
 * without gameplay drag handlers or settings controls.
 */
export default function ArticleBoard({
  fen,
  title,
  caption,
  playingWhite = true,
  boardSize: requestedBoardSize = DEFAULT_SIZE,
  edgeColor,
  arrows = [],
  highlights = [],
  presentation = "body",
}: ArticleBoardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [boardSize, setBoardSize] = useState(requestedBoardSize);

  useEffect(() => {
    if (!containerRef.current || typeof window === "undefined") return;

    const updateSize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      setBoardSize(Math.max(MINIMUM_SIZE, Math.min(requestedBoardSize, width)));
    };

    updateSize();

    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateSize);
    observer?.observe(containerRef.current);
    window.addEventListener("resize", updateSize);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateSize);
    };
  }, [requestedBoardSize]);

  const isHero = presentation === "hero";
  const articleArrows = arrows.map((arrow) => ({
    ...arrow,
    color: arrow.color || DEFAULT_ARROW_COLOR,
  }));

  return (
    <BlogBoardFrame
      title={title}
      caption={caption}
      className={isHero ? "my-0 mb-10" : undefined}
      cardClassName={
        isHero
          ? "mx-auto rounded-lg bg-white shadow-sm ring-1 ring-slate-200"
          : undefined
      }
      cardStyle={edgeColor ? { backgroundColor: edgeColor } : undefined}
    >
      <div ref={containerRef} className="flex justify-center w-full md:w-fit">
        <StaticBoard
          fen={fen}
          boardSize={boardSize}
          playingWhite={playingWhite}
          arrows={articleArrows}
          highlights={highlights}
          showCoordinates
          ariaLabel={title || caption || "Chess position"}
        />
      </div>
    </BlogBoardFrame>
  );
}
